<template>
  <div>
    <label v-if="label" class="block text-sm font-medium mb-1">{{ label }}</label>
    <div v-for="(item, index) in displayItems" :key="index" class="flex items-center mb-2">
      <input
        type="text"
        :value="item"
        :placeholder="placeholder"
        @v-model="displayItems[index]"
        class="input input-bordered flex-1 mr-2" />
      <button
        type="button"
        class="btn btn-sm btn-error"
        @click="remove(index)"
        :disabled="displayItems.length <= 1"
        aria-label="Remove item">
        &times;
      </button>
    </div>
    <button type="button" class="btn btn-sm btn-primary mt-2" @click="add()">Add {{ itemLabel }}</button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { PropType } from "vue";

/**
 * Props:
 * - modelValue: bound array of strings
 * - label: field label
 * - itemLabel: singular name for each item
 * - placeholder: input placeholder
 */
const props = defineProps({
  modelValue: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  label: { type: String, default: "" },
  itemLabel: { type: String, default: "item" },
  placeholder: { type: String, default: "" },
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string[]): void;
}>();

// Always show at least one input slot
const displayItems = computed<string[]>(() => (props.modelValue.length > 0 ? props.modelValue : [""]));

/** Handle user typing in an input */
function onInput(index: number, value: string) {
  // Clone existing values or start with a single blank
  const items = props.modelValue.length > 0 ? [...props.modelValue] : [""];
  items[index] = value;
  // If typing into the last slot and it's not empty, add a new blank one
  if (index === items.length - 1 && value !== "") {
    items.push("");
  }
  emit("update:modelValue", items);
}

/** Add a new blank item */
function add() {
  const items = props.modelValue.length > 0 ? [...props.modelValue] : [""];
  items.push("");
  emit("update:modelValue", items);
}

/** Remove an item by index */
function remove(index: number) {
  const items = props.modelValue.length > 0 ? [...props.modelValue] : [""];
  items.splice(index, 1);
  emit("update:modelValue", items);
}
</script>

<style scoped>
/* Minimal styling; leverage shared utilities */
</style>
